import { useState, useEffect, type Key } from "react";
import { useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/common/components/ui/card";
import { Button } from "@/common/components/ui/button";
import { Input } from "@/common/components/ui/input";
import { Badge } from "@/common/components/ui/badge";
import {
  Search,
  Eye,
  User,
  Download,
  ShoppingCart,
  RefreshCw,
  Copy,
  Check,
  Edit,
} from "lucide-react";
import { Popover } from "antd";
import { useToast } from "@/hooks/use-toast";
import AntDTable from "@/common/components/ui/antd-table";
import { ScrollArea } from "@/common/components/ui/scroll-area";
import { format } from "date-fns";
import finEaseHomeNavigationRoutes from "@/projects/FinEaseHomeAndAdmin/utils/navigationRoutes";

import GoldService from "@/services/FinEaseHomeAndAdmin/APIs/GoldService";
import { GoldOrder } from "@/utils/types/Gold";
import {
  downloadGoldOrdersExcel,
  getOrderRowKey,
} from "./goldOrdersExcelExport";

const GoldOrders = () => {
  const navigate = useNavigate();
  const [orders, setOrders] = useState<GoldOrder[]>([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [copiedId, setCopiedId] = useState<string | null>(null);
  const [selectedRowKeys, setSelectedRowKeys] = useState<Key[]>([]);
  const { toast } = useToast();

  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopiedId(text);
    setTimeout(() => setCopiedId(null), 2000);
    toast({
      title: "Copied",
      description: "ID copied to clipboard",
    });
  };

  const getInitials = (name: string) => {
    return name
      .split(" ")
      .map(n => n[0])
      .join("")
      .toUpperCase()
      .substring(0, 2);
  };

  const fetchOrders = async () => {
    try {
      setLoading(true);
      const res = await GoldService.getOrders(page, pageSize);
      if (res?.results) {
        setOrders(res.results);
      } else if (res?.data?.results) {
        setOrders(res.data.results);
      } else if (Array.isArray(res)) {
        setOrders(res);
      } else if (Array.isArray(res?.data)) {
        setOrders(res.data);
      }
    } catch (_err) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to fetch orders",
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchOrders();
  }, []);

  const handleExport = () => {
    if (selectedRowKeys.length === 0) {
      toast({
        variant: "destructive",
        title: "No orders selected",
        description:
          "Use the checkboxes to select one or more orders, then click Export.",
      });
      return;
    }

    const selected = filteredOrders.filter(o =>
      selectedRowKeys.includes(getOrderRowKey(o))
    );

    if (selected.length === 0) {
      toast({
        variant: "destructive",
        title: "Nothing to export",
        description: "Selected rows are not in the current filtered list.",
      });
      return;
    }

    const fname = `gold_orders_${new Date().toISOString().split("T")[0]}`;
    downloadGoldOrdersExcel(selected, fname);
    toast({
      title: "Exported",
      description: `${selected.length} order(s) saved as Excel (.xlsx).`,
    });
  };

  const filteredOrders = orders.filter(
    o =>
      Boolean(o.order_id?.toLowerCase().includes(searchTerm.toLowerCase())) ||
      Boolean(
        o.id?.toString().toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      Boolean(
        o.customer_detail?.full_name
          ?.toLowerCase()
          .includes(searchTerm.toLowerCase()) ??
        o.customer_name?.toLowerCase().includes(searchTerm.toLowerCase())
      ) ||
      Boolean(o.mmtc_order_id?.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  const total = filteredOrders.length;

  const getStatusColor = (status: string) => {
    switch (status) {
      case "new_order":
        return "bg-blue-100 text-blue-700";
      case "order_confirmed":
        return "bg-cyan-100 text-cyan-700";
      case "order_processing":
        return "bg-amber-100 text-amber-700";
      case "order_dispatched":
        return "bg-purple-100 text-purple-700";
      case "order_delivered":
        return "bg-green-100 text-green-700";
      case "order_cancelled":
        return "bg-red-100 text-red-700";
      default:
        return "bg-gray-100 text-gray-700";
    }
  };

  const columns = [
    {
      title: "Order Info",
      key: "order_id",
      width: 180,
      render: (_: any, order: any) => (
        <div className="flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <span className="font-bold text-slate-900">
              {order.order_id ?? order.id ?? "-"}
            </span>
            <button
              onClick={() => handleCopy(order.order_id ?? order.id)}
              className="text-slate-400 transition-colors hover:text-primary"
            >
              {copiedId === (order.order_id ?? order.id) ? (
                <Check className="h-3 w-3 text-green-500" />
              ) : (
                <Copy className="h-3 w-3" />
              )}
            </button>
          </div>
          <div className="flex flex-col gap-0.5">
            <span className="text-[11px] font-medium uppercase tracking-tight text-slate-500">
              MMTC: {order.mmtc_order_id ?? "-"}
            </span>
            <span className="text-[11px] text-slate-400">
              {order.order_date
                ? format(new Date(order.order_date), "dd MMM yyyy, hh:mm a")
                : "-"}
            </span>
          </div>
        </div>
      ),
    },
    {
      title: "Customer",
      key: "customer",
      width: 250,
      render: (_: any, order: any) => {
        const name =
          order.customer_detail?.full_name ?? order.customer_name ?? "-";
        const email =
          order.customer_detail?.email ?? order.customer_email ?? "-";
        return (
          <div className="flex items-center gap-3">
            <div className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full border border-slate-200 bg-slate-100 text-xs font-bold text-slate-600">
              {name !== "-" ? getInitials(name) : <User className="h-4 w-4" />}
            </div>
            <div className="flex min-w-0 flex-col">
              <span className="truncate font-semibold text-slate-800">
                {name}
              </span>
              <span className="truncate text-xs text-slate-400">{email}</span>
            </div>
          </div>
        );
      },
    },
    {
      title: "Product Summary",
      key: "product",
      width: 250,
      render: (_: any, order: any) => {
        const items = order.items ?? order.line_items ?? [];
        const totalItems = items.length;
        const totalQuantity = items.reduce(
          (sum: number, item: any) => sum + (item.quantity ?? 0),
          0
        );
        const totalWeight = items.reduce(
          (sum: number, item: any) =>
            sum +
            parseFloat(item.weight_grams ?? item.weight ?? "0") *
              (item.quantity ?? 1),
          0
        );
        const firstItem = items[0];

        return (
          <div className="flex items-center gap-3">
            <div className="h-12 w-12 flex-shrink-0 overflow-hidden rounded-lg border border-slate-100 bg-slate-50 shadow-sm">
              {firstItem.product_image ? (
                <img
                  src={firstItem.product_image}
                  alt={firstItem.product_name}
                  className="h-full w-full object-cover"
                />
              ) : (
                <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-300">
                  IMG
                </div>
              )}
            </div>
            <div className="flex min-w-0 flex-col gap-1.5">
              <div className="flex items-center gap-1.5 overflow-hidden">
                <span className="truncate text-sm font-bold text-slate-800">
                  {firstItem?.product_name ?? "Unknown Product"}
                </span>
                {totalItems > 1 && (
                  <Popover
                    content={
                      <ScrollArea className="h-auto w-80 pr-4">
                        <div className="flex flex-col gap-4 p-1">
                          {items.map((item: any) => (
                            <div
                              key={item.id}
                              className="flex items-center gap-3 border-b border-slate-50 pb-3 last:border-0 last:pb-0"
                            >
                              <div className="h-10 w-10 flex-shrink-0 overflow-hidden rounded-md border border-slate-100 bg-slate-50">
                                {item.product_image ? (
                                  <img
                                    src={item.product_image}
                                    alt={item.product_name}
                                    className="h-full w-full object-cover"
                                  />
                                ) : (
                                  <div className="flex h-full w-full items-center justify-center text-[10px] text-slate-300">
                                    IMG
                                  </div>
                                )}
                              </div>
                              <div className="flex min-w-0 flex-col gap-0.5">
                                <p className="truncate text-xs font-bold text-slate-800">
                                  {item.product_name}
                                </p>
                                <div className="flex items-center gap-2">
                                  <span className="text-[10px] font-medium text-slate-500">
                                    {item.weight_grams ?? item.weight ?? "0"}g
                                  </span>
                                  <span className="h-1 w-1 rounded-full bg-slate-200" />
                                  <span className="text-[10px] font-bold text-primary">
                                    Qty: {item.quantity ?? 1}
                                  </span>
                                </div>
                              </div>
                            </div>
                          ))}
                        </div>
                      </ScrollArea>
                    }
                    title={
                      <span className="text-xs font-bold uppercase tracking-wider text-slate-500">
                        Total Items ({totalItems})
                      </span>
                    }
                    trigger="click"
                  >
                    <Badge className="h-5 cursor-pointer rounded-full border-0 bg-primary/10 px-2 text-[10px] font-bold text-primary transition-colors hover:bg-primary/20">
                      +{totalItems - 1}
                    </Badge>
                  </Popover>
                )}
              </div>
              <div className="flex items-center gap-2">
                <span className="rounded border border-amber-100/50 bg-amber-50 px-1.5 py-0.5 text-[11px] font-bold text-amber-600">
                  {totalWeight.toFixed(2)}g
                </span>
                <span className="h-1 w-1 rounded-full bg-slate-300" />
                <span className="text-[11px] font-medium text-slate-500">
                  {totalQuantity} Units
                </span>
              </div>
            </div>
          </div>
        );
      },
    },
    {
      title: "Amount",
      key: "amount",
      width: 200,
      align: "left" as const,
      render: (_: any, order: any) => (
        <div className="flex flex-col items-start">
          <span className="text-sm font-black text-slate-900">
            ₹
            {parseFloat(
              order.grand_total ?? order.final_amount ?? "0"
            ).toLocaleString("en-IN")}
          </span>
          <span className="text-[10px] font-bold uppercase text-slate-400">
            Net Total
          </span>
        </div>
      ),
    },
    {
      title: "Status",
      key: "payment_status",
      align: "center" as const,
      width: 200,
      render: (_: any, order: any) => (
        <div className="flex flex-col gap-2">
          <div className="flex items-center justify-between gap-4">
            <span className="text-[10px] font-bold uppercase text-slate-400">
              Payment:
            </span>
            <Badge
              variant="outline"
              className={`min-w-[80px] justify-center border-0 py-0.5 text-[9px] font-black uppercase tracking-wider ${(order.payment_status ?? "PENDING") === "SUCCESS" ? "bg-emerald-50 text-emerald-600" : "bg-orange-50 text-orange-600"}`}
            >
              {order.payment_status ?? "PENDING"}
            </Badge>
          </div>
          <div className="flex items-center justify-between gap-4">
            <span className="text-[10px] font-bold uppercase text-slate-400">
              Order:
            </span>
            <Badge
              variant="outline"
              className={`min-w-[80px] justify-center border-0 py-0.5 text-[9px] font-black uppercase tracking-wider ${getStatusColor(order.order_status ?? "new_order")}`}
            >
              {order.order_status?.replace("_", " ") ?? "new_order"}
            </Badge>
          </div>
        </div>
      ),
    },
    {
      title: "Actions",
      key: "action",
      align: "center",
      width: 180,
      fixed: "right" as const,
      render: (_: any, order: any) => {
        const detailPath =
          finEaseHomeNavigationRoutes.PHYSICAL_GOLD_ORDER_DETAILS(order.id);
        const editPath = finEaseHomeNavigationRoutes.PHYSICAL_GOLD_EDIT_ORDER(
          order.id
        );
        return (
          <div className="flex flex-wrap items-center justify-center gap-1.5">
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigate(detailPath)}
              title="View Order"
            >
              <Eye className="h-4 w-4" />
            </Button>
            <Button
              variant="ghost"
              size="icon"
              className="h-8 w-8"
              onClick={() => navigate(editPath)}
              title="Edit Order"
            >
              <Edit className="h-4 w-4" />
            </Button>
          </div>
        );
      },
    },
  ] as any;

  return (
    <div className="min-h-screen space-y-6 bg-gray-50 p-6 pb-20">
      <div className="relative overflow-hidden rounded-2xl bg-gradient-to-r from-secondary via-secondary/95 to-primary/80 p-6 md:p-8">
        <div className="absolute inset-0 bg-gradient-to-br from-white/5 to-transparent" />
        <svg
          className="absolute right-0 top-0 h-full w-1/3 text-white/5"
          viewBox="0 0 100 100"
          preserveAspectRatio="none"
        >
          <polygon fill="currentColor" points="50,0 100,0 100,100 0,100" />
        </svg>
        <div className="relative flex flex-col justify-between gap-4 md:flex-row md:items-center">
          <div className="flex items-center gap-4">
            <div className="rounded-xl bg-white/10 p-3 backdrop-blur-sm">
              <ShoppingCart className="h-6 w-6 text-white" />
            </div>
            <div>
              <h1 className="font-display text-2xl font-bold text-white">
                Gold Orders
              </h1>
              <p className="text-white/70">
                View, manage, and process all physical gold orders
              </p>
            </div>
          </div>
          <div className="flex flex-wrap gap-3">
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={fetchOrders}
              disabled={loading}
            >
              <RefreshCw
                className={`h-4 w-4 ${loading ? "animate-spin" : ""}`}
              />{" "}
              Sync Data
            </Button>
            <Button
              variant="outline"
              className="gap-2 border-white/20 bg-white/10 text-white hover:bg-white/20"
              onClick={handleExport}
              disabled={selectedRowKeys.length === 0}
            >
              <Download className="h-4 w-4" />
              Export
              {selectedRowKeys.length > 0 && (
                <span className="ml-1 rounded-full bg-white/25 px-1.5 py-0.5 text-[11px] font-semibold leading-none">
                  {selectedRowKeys.length}
                </span>
              )}
            </Button>
          </div>
        </div>
      </div>

      <Card className="border-none shadow-sm">
        <CardContent className="flex flex-col justify-between gap-4 p-4 md:flex-row md:items-center">
          <div className="relative max-w-md flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Search by Order ID, MMTC ID, or Customer name..."
              className="border-gray-100 bg-gray-50/50 pl-9 placeholder:text-[12px] placeholder:font-normal placeholder:text-slate-400 focus:bg-white"
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
          </div>
        </CardContent>
      </Card>

      <AntDTable
        showSettings
        columns={columns}
        data={filteredOrders}
        loading={loading}
        scroll={{ x: "max-content" }}
        rowSelection={{
          selectedRowKeys,
          onChange: keys => setSelectedRowKeys(keys),
          preserveSelectedRowKeys: true,
          columnWidth: 48,
        }}
        pagination={{
          current: page,
          pageSize,
          total,
          showSizeChanger: true,
          pageSizeOptions: ["10", "20", "50", "100"],
          onChange: (nextPage, nextSize) => {
            setPage(nextPage);
            setPageSize(nextSize ?? 10);
          },
          showTotal: (totalCount, range) =>
            `${range[0]}-${range[1]} of ${totalCount} orders`,
        }}
        rowKey={(record: any) => String(record.order_id ?? record.id)}
      />
    </div>
  );
};

export default GoldOrders;
